/**
 * `useProjectAttendance` — live subscription to attendance records
 * for a project on a single date.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/attendance/useAttendance.ts
 *
 * Same Firestore query (`attendance where orgId == X AND projectId == Y
 * AND date == YYYY-MM-DD`). Records sorted by labour name.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import type { AttendanceRecord } from '@/lib/attendance';
import { db } from '@/lib/firebase';

export type AttendanceSummary = {
  present: number;
  absent: number;
  halfDay: number;
  paidLeave: number;
  weekOff: number;
  total: number;
};

export type UseProjectAttendanceResult = {
  data: AttendanceRecord[];
  loading: boolean;
  summary: AttendanceSummary;
};

export function useProjectAttendance(
  orgId: string | null | undefined,
  projectId: string | undefined,
  dateString: string,
): UseProjectAttendanceResult {
  const [data, setData] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !projectId || !dateString) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'attendance'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
        where('date', '==', dateString),
      ),
      (snap) => {
        const rows: AttendanceRecord[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<AttendanceRecord, 'id'>),
        }));
        rows.sort((a, b) => a.labourName.localeCompare(b.labourName));
        setData(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useProjectAttendance] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId, projectId, dateString]);

  const summary = useMemo<AttendanceSummary>(() => {
    let present = 0;
    let absent = 0;
    let halfDay = 0;
    let paidLeave = 0;
    let weekOff = 0;
    for (const r of data) {
      if (r.status === 'present') present++;
      else if (r.status === 'absent') absent++;
      else if (r.status === 'half_day') halfDay++;
      else if (r.status === 'paid_leave') paidLeave++;
      else if (r.status === 'week_off') weekOff++;
    }
    return {
      present,
      absent,
      halfDay,
      paidLeave,
      weekOff,
      total: data.length,
    };
  }, [data]);

  return { data, loading, summary };
}

/**
 * Subscribe to ALL attendance for a project (no date filter). Used
 * by the right-rail monthly summary so the customer can scroll
 * through months without re-querying. The dataset stays small
 * (~30-100 docs per month per project) so a single subscription is
 * fine.
 */
export function useProjectAttendanceAll(
  orgId: string | null | undefined,
  projectId: string | undefined,
): { data: AttendanceRecord[]; loading: boolean } {
  const [data, setData] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !projectId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'attendance'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
      ),
      (snap) => {
        setData(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<AttendanceRecord, 'id'>),
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useProjectAttendanceAll] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId, projectId]);

  return { data, loading };
}
